import { getUsState, usMapViewBox, usStateOptions, usStatePaths } from "@crescentParts/Components/usStatePaths";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import styled, { css, keyframes } from "styled-components";

interface VideoItem extends HasFields {
    fields: {
        videoUrl: string;
        videoTitle: string;
        videoDescription: string;
        orientation: string;
    };
}

interface LocationItem extends HasFields {
    fields: {
        state: string;
        label: string;
        stateTitle: string;
        useDefaultColor: boolean;
        stateColor: string;
        selectedColor: string;
        pinIcon: string;
        pinIconSize: number;
        xDirection: string;
        xOffset: number;
        yDirection: string;
        yOffset: number;
        iconXDirection: string;
        iconXOffset: number;
        iconYDirection: string;
        iconYOffset: number;
        videos: VideoItem[];
        hideLabel: boolean;
    };
}

interface ExtraStateItem extends HasFields {
    fields: {
        state: string;
    };
}

const enum fields {
    heading = "heading",
    subheading = "subheading",
    mapScope = "mapScope",
    mapPadding = "mapPadding",
    extraStates = "extraStates",
    maxWidth = "maxWidth",
    stateFill = "stateFill",
    stateBorder = "stateBorder",
    highlightFill = "highlightFill",
    selectedFill = "selectedFill",
    markerSize = "markerSize",
    markerColor = "markerColor",
    markerIcon = "markerIcon",
    markerIconSize = "markerIconSize",
    labelColor = "labelColor",
    labelFontSize = "labelFontSize",
    showLabels = "showLabels",
    videoDisplay = "videoDisplay",
    locations = "locations",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.mapScope]: string;
        [fields.mapPadding]: number;
        [fields.extraStates]: ExtraStateItem[];
        [fields.maxWidth]: number;
        [fields.stateFill]: string;
        [fields.stateBorder]: string;
        [fields.highlightFill]: string;
        [fields.selectedFill]: string;
        [fields.markerSize]: number;
        [fields.markerColor]: string;
        [fields.markerIcon]: string;
        [fields.markerIconSize]: number;
        [fields.labelColor]: string;
        [fields.labelFontSize]: number;
        [fields.showLabels]: boolean;
        [fields.videoDisplay]: string;
        [fields.locations]: LocationItem[];
    };
}

interface LocationVideoMapStyles {
    wrapper?: InjectableCss;
    heading?: InjectableCss;
    subheading?: InjectableCss;
    selectedTitle?: InjectableCss;
    modal?: ModalPresentationProps;
    videoDescription?: InjectableCss;
}

export const locationVideoMapStyles: LocationVideoMapStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 40px 16px;
                    `,
                    null,
                    null,
                    css`
                        padding: 60px 40px;
                    `,
                ])}
        `,
    },
    heading: {
        css: css`
            text-align: center;
            margin: 0 0 8px;
        `,
    },
    subheading: {
        css: css`
            text-align: center;
            margin: 0 0 24px;
            max-width: 720px;
        `,
    },
    selectedTitle: {
        css: css`
            text-align: center;
            font-weight: 700;
            margin: 0 0 16px;
        `,
    },
    modal: {
        size: 900,
        cssOverrides: {
            modalContent: css`
                padding: 16px 24px 24px;
            `,
        },
    },    videoDescription: {
        css: css`
            margin-top: 12px;
        `,
    },
};

const styles = locationVideoMapStyles;

const MapSvg = styled.svg`
    width: 100%;
    height: auto;
    display: block;

    path:focus,
    g:focus,
    path:focus-visible,
    g:focus-visible {
        outline: none;
    }
`;

const ContentRow = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 32px;
    width: 100%;

    @media (max-width: 991px) {
        flex-direction: column;
        gap: 24px;
    }
`;

const MapColumn = styled.div<{ $maxWidth: string }>`
    flex: 1 1 auto;
    min-width: 0;
    max-width: ${({ $maxWidth }) => $maxWidth};
`;

const ConnectorLayer = styled.svg`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
    animation: ${keyframes`
        from { opacity: 0; }
        to { opacity: 1; }
    `} 0.4s ease-out both;
`;

const marchingDashes = keyframes`
    to {
        stroke-dashoffset: -16;
    }
`;

const ConnectorPath = styled.path`
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-dasharray: 6 10;
    animation: ${marchingDashes} 0.9s linear infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const cardIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(24px) scale(0.96);
    }
    to {
        opacity: 1;
        transform: translateX(0) scale(1);
    }
`;

const VideoCard = styled.div`
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    width: 340px;
    max-width: 100%;
    padding: 16px;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
    animation: ${cardIn} 0.45s cubic-bezier(0.2, 0.8, 0.3, 1) both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const CardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
`;

const CardTitle = styled.span`
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
`;

const CloseButton = styled.button`
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #f0f0f0;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;

    &:hover,
    &:focus-visible {
        background: #e0e0e0;
        outline: none;
    }
`;

const StatePath = styled.path<{ $fill: string; $border: string }>`
    fill: ${({ $fill }) => $fill};
    stroke: ${({ $border }) => $border};
    stroke-width: 1;
    stroke-linejoin: round;
`;

const HighlightPath = styled.path<{ $fill: string; $border: string }>`
    fill: ${({ $fill }) => $fill};
    stroke: ${({ $border }) => $border};
    stroke-width: 1;
    stroke-linejoin: round;
    cursor: pointer;
    outline: none;
    transition: fill 0.25s ease-in-out, filter 0.15s ease-in-out;

    &:focus,
    &:focus-visible,
    &:active {
        outline: none;
    }

    &:hover,
    &:focus-visible {
        filter: brightness(1.1);
    }
`;

const MarkerGroup = styled.g`
    cursor: pointer;
    outline: none;

    &:focus,
    &:focus-visible,
    &:active {
        outline: none;
    }
`;

const pulse = keyframes`
    0% {
        transform: scale(0.85);
        opacity: 0.7;
    }
    70% {
        transform: scale(1.6);
        opacity: 0;
    }
    100% {
        transform: scale(1.6);
        opacity: 0;
    }
`;

const MarkerRing = styled.circle`
    fill: none;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${pulse} 2.2s cubic-bezier(0.2, 0.6, 0.4, 1) infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
        opacity: 0.6;
    }
`;

const MarkerRingStatic = styled.circle`
    fill: none;
    opacity: 0.55;
    transition: opacity 0.15s ease-in-out;

    ${MarkerGroup}:hover &,
    ${MarkerGroup}:focus-visible & {
        opacity: 1;
    }
`;

const MarkerLabel = styled.text<{ $fontSize: number }>`
    font-size: ${({ $fontSize }) => $fontSize}px;
    font-weight: 700;
    text-anchor: middle;
    text-transform: uppercase;
    pointer-events: none;
`;

const VideoFrame = styled.div<{ $portrait: boolean }>`
    position: relative;
    width: 100%;
    max-width: ${({ $portrait }) => ($portrait ? "min(360px, 100%)" : "100%")};
    margin: 0 auto;
    padding-top: ${({ $portrait }) => ($portrait ? "min(177.78%, 70vh)" : "56.25%")};

    iframe,
    video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
    }
`;

const Playlist = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
`;

const PlaylistButton = styled.button<{ $active: boolean }>`
    padding: 8px 14px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? 700 : 400)};
    border: 1px solid ${({ $active }) => ($active ? "#00843d" : "#c9c9c9")};
    background: ${({ $active }) => ($active ? "#00843d" : "#ffffff")};
    color: ${({ $active }) => ($active ? "#ffffff" : "#1c1c1c")};

    &:hover,
    &:focus-visible {
        border-color: #00843d;
        outline: none;
    }
`;

const toNumber = (value: string | number | undefined, fallback: number) => {
    const parsed = typeof value === "number" ? value : parseFloat(value ?? "");
    return isNaN(parsed) ? fallback : parsed;
};

const getLabel = (location: LocationItem) => location.fields.label || getUsState(location.fields.state)?.name || "";

const getStateFill = (location: LocationItem, defaultFill: string) =>
    location.fields.useDefaultColor === false && location.fields.stateColor ? location.fields.stateColor : defaultFill;

// IntegerField cannot accept negative input, so direction is captured separately.
const getNudge = (amount: number, direction: string, negativeDirection: string) =>
    toNumber(amount, 0) * (direction === negativeDirection ? -1 : 1);

const isFileVideo = (url: string) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);

const isShortsUrl = (url: string) => /youtube\.com\/shorts\//i.test(url);

const isPortrait = (video: VideoItem | undefined) => {
    if (!video) {
        return false;
    }
    if (video.fields.orientation === "portrait") {
        return true;
    }
    if (video.fields.orientation === "landscape") {
        return false;
    }
    return isShortsUrl(video.fields.videoUrl);
};

/** Converts common YouTube/Vimeo share links into their embeddable form; other URLs pass through. */
const toEmbedUrl = (url: string) => {
    if (!url) {
        return "";
    }

    const youTubeMatch = url.match(
        /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/,
    );
    if (youTubeMatch) {
        return `https://www.youtube.com/embed/${youTubeMatch[1]}?autoplay=1&rel=0`;
    }

    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    }

    return url;
};

const [, , viewBoxWidth, viewBoxHeight] = usMapViewBox.split(" ").map(Number);

const LocationVideoMap: FC<OwnProps> = ({ fields }) => {
    const [activeLocation, setActiveLocation] = React.useState<LocationItem | undefined>(undefined);
    const [activeVideoIndex, setActiveVideoIndex] = React.useState(0);
    // Kept separate from activeLocation so the title stays visible after the modal is closed.
    const [selectedTitle, setSelectedTitle] = React.useState("");
    const [centers, setCenters] = React.useState<{ [stateId: string]: { x: number; y: number } }>({});
    const [boxes, setBoxes] = React.useState<{ [stateId: string]: DOMRect }>({});
    const [connector, setConnector] = React.useState<{ x1: number; y1: number; x2: number; y2: number } | undefined>(
        undefined,
    );
    const pathRefs = React.useRef<{ [stateId: string]: SVGPathElement | null }>({});
    const rowRef = React.useRef<HTMLDivElement | null>(null);
    const svgRef = React.useRef<SVGSVGElement | null>(null);
    const cardRef = React.useRef<HTMLDivElement | null>(null);

    const openLocation = (location: LocationItem) => {
        setActiveVideoIndex(0);
        setSelectedTitle(location.fields.stateTitle || getLabel(location));
        setActiveLocation(location);
    };

    const locations = fields.locations ?? [];
    const configuredStates = locations.map(location => location.fields.state).join(",");
    const extraStates = (fields.extraStates ?? []).map(item => item.fields.state).join(",");
    const showFullMap = fields.mapScope !== "selected";
    const visibleIds = showFullMap
        ? usStatePaths.map(state => state.id).join(",")
        : [...new Set(`${configuredStates},${extraStates}`.split(",").filter(Boolean))].join(",");
    const visibleStates = showFullMap
        ? usStatePaths
        : usStatePaths.filter(state => visibleIds.split(",").indexOf(state.id) > -1);

    // Pin coordinates and the fitted viewBox are derived from the rendered geometry rather than hardcoded.
    React.useEffect(() => {
        const nextCenters: { [stateId: string]: { x: number; y: number } } = {};
        const nextBoxes: { [stateId: string]: DOMRect } = {};
        visibleIds
            .split(",")
            .filter(Boolean)
            .forEach(stateId => {
                const element = pathRefs.current[stateId];
                if (element) {
                    const box = element.getBBox();
                    nextBoxes[stateId] = box;
                    nextCenters[stateId] = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
                }
            });
        setBoxes(nextBoxes);
        setCenters(nextCenters);
    }, [visibleIds]);

    const padding = toNumber(fields.mapPadding, 20);
    const viewBox = React.useMemo(() => {
        if (showFullMap) {
            return usMapViewBox;
        }

        const rects = visibleIds
            .split(",")
            .filter(Boolean)
            .map(stateId => boxes[stateId])
            .filter(Boolean);
        if (!rects.length) {
            return usMapViewBox;
        }

        const minX = Math.min(...rects.map(rect => rect.x));
        const minY = Math.min(...rects.map(rect => rect.y));
        const maxX = Math.max(...rects.map(rect => rect.x + rect.width));
        const maxY = Math.max(...rects.map(rect => rect.y + rect.height));
        return `${minX - padding} ${minY - padding} ${maxX - minX + padding * 2} ${maxY - minY + padding * 2}`;
    }, [showFullMap, visibleIds, boxes, padding]);

    const [viewBoxMinX, viewBoxMinY, viewBoxWidth, viewBoxHeight] = viewBox.split(" ").map(Number);

    const markerSize = toNumber(fields.markerSize, 7);
    const stateFill = fields.stateFill || "#f2f2f2";
    const stateBorder = fields.stateBorder || "#ffffff";
    const highlightFill = fields.highlightFill || "#9fd6c4";
    const selectedFill = fields.selectedFill || "";
    const markerColor = fields.markerColor || "#1c1c1c";
    const labelColor = fields.labelColor || "#00843d";
    const labelFontSize = toNumber(fields.labelFontSize, 11);

    const activeVideos = activeLocation?.fields.videos ?? [];
    const activeVideo = activeVideos[activeVideoIndex];
    const videoUrl = activeVideo?.fields.videoUrl ?? "";
    const videoTitle = activeVideo?.fields.videoTitle || (activeLocation ? getLabel(activeLocation) : "");

    const usePanel = fields.videoDisplay !== "modal";
    const activeCenter = activeLocation ? centers[activeLocation.fields.state] : undefined;
    const activePin =
        activeLocation && activeCenter
            ? {
                  x: activeCenter.x + getNudge(activeLocation.fields.xOffset, activeLocation.fields.xDirection, "left"),
                  y: activeCenter.y + getNudge(activeLocation.fields.yOffset, activeLocation.fields.yDirection, "up"),
              }
            : undefined;

    React.useLayoutEffect(() => {
        if (!usePanel) {
            return;
        }

        const measure = () => {
            const row = rowRef.current;
            const svg = svgRef.current;
            const card = cardRef.current;
            if (!row || !svg || !card || !activePin) {
                setConnector(undefined);
                return;
            }

            const rowBox = row.getBoundingClientRect();
            const svgBox = svg.getBoundingClientRect();
            const cardBox = card.getBoundingClientRect();

            // The stacked mobile layout puts the card below the map, where a connector makes no sense.
            if (cardBox.left < svgBox.left + svgBox.width / 2) {
                setConnector(undefined);
                return;
            }

            setConnector({
                x1: svgBox.left - rowBox.left + ((activePin.x - viewBoxMinX) / viewBoxWidth) * svgBox.width,
                y1: svgBox.top - rowBox.top + ((activePin.y - viewBoxMinY) / viewBoxHeight) * svgBox.height,
                x2: cardBox.left - rowBox.left,
                y2: cardBox.top - rowBox.top + cardBox.height / 2,
            });
        };

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [usePanel, activePin?.x, activePin?.y, activeVideoIndex, videoUrl, viewBox]);

    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.heading && (
                <Typography variant="h2" {...styles.heading}>
                    {fields.heading}
                </Typography>
            )}
            {fields.subheading && (
                <Typography variant="p" {...styles.subheading}>
                    {fields.subheading}
                </Typography>
            )}
            <ContentRow ref={rowRef}>
                <MapColumn $maxWidth={fields.maxWidth ? `${fields.maxWidth}px` : "100%"}>
                    {selectedTitle && (
                        <Typography variant="h4" {...styles.selectedTitle}>
                            {selectedTitle}
                        </Typography>
                    )}
                    <MapSvg
                        ref={svgRef}
                        viewBox={viewBox}
                        role="img"
                        aria-label={fields.heading || "United States map"}
                    >
                    {visibleStates.map(state => {
                        const location = locations.find(item => item.fields.state === state.id);
                        if (!location) {
                            return (
                                <StatePath
                                    key={state.id}
                                    ref={(element: SVGPathElement | null) => {
                                        pathRefs.current[state.id] = element;
                                    }}
                                    d={state.d}
                                    $fill={stateFill}
                                    $border={stateBorder}
                                    aria-hidden="true"
                                />
                            );
                        }

                        return (
                            <HighlightPath
                                key={state.id}
                                ref={(element: SVGPathElement | null) => {
                                    pathRefs.current[state.id] = element;
                                }}
                                d={state.d}
                                $fill={
                                    (activeLocation?.fields.state === state.id &&
                                        (location.fields.selectedColor || selectedFill)) ||
                                    getStateFill(location, highlightFill)
                                }
                                $border={stateBorder}
                                role="button"
                                tabIndex={0}
                                aria-label={getLabel(location)}
                                onClick={() => openLocation(location)}
                                onKeyDown={(event: React.KeyboardEvent<SVGPathElement>) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        openLocation(location);
                                    }
                                }}
                            >
                                <title>{getLabel(location)}</title>
                            </HighlightPath>
                        );
                    })}
                    {locations.map((location, index) => {
                        const center = centers[location.fields.state];
                        if (!center) {
                            return null;
                        }

                        const cx = center.x + getNudge(location.fields.xOffset, location.fields.xDirection, "left");
                        const cy = center.y + getNudge(location.fields.yOffset, location.fields.yDirection, "up");
                        const label = getLabel(location);
                        const pinIcon = location.fields.pinIcon || fields.markerIcon;
                        const iconSize = toNumber(location.fields.pinIconSize || fields.markerIconSize, markerSize * 3);
                        const iconX = cx + getNudge(location.fields.iconXOffset, location.fields.iconXDirection, "left");
                        const iconY = cy + getNudge(location.fields.iconYOffset, location.fields.iconYDirection, "up");

                        return (
                            <MarkerGroup
                                key={`${location.fields.state}-${index}`}
                                role="button"
                                tabIndex={0}
                                aria-label={label}
                                onClick={() => openLocation(location)}
                                onKeyDown={(event: React.KeyboardEvent<SVGGElement>) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        openLocation(location);
                                    }
                                }}
                            >
                                <MarkerRing cx={cx} cy={cy} r={markerSize + 4} stroke={labelColor} strokeWidth={2} />
                                <MarkerRingStatic
                                    cx={cx}
                                    cy={cy}
                                    r={markerSize + 2}
                                    stroke={labelColor}
                                    strokeWidth={1.5}
                                />
                                {pinIcon ? (
                                    <image
                                        href={pinIcon}
                                        x={iconX - iconSize / 2}
                                        y={iconY + markerSize - iconSize + 5}
                                        width={iconSize}
                                        height={iconSize}
                                        preserveAspectRatio="xMidYMid meet"
                                    />
                                ) : (
                                    <circle cx={cx} cy={cy} r={markerSize} fill={markerColor} />
                                )}
                                {fields.showLabels && !location.fields.hideLabel && label && (
                                    <MarkerLabel
                                        x={cx}
                                        y={cy + markerSize + labelFontSize + 3}
                                        fill={labelColor}
                                        $fontSize={labelFontSize}
                                    >
                                        {label}
                                    </MarkerLabel>
                                )}
                            </MarkerGroup>
                        );
                    })}
                </MapSvg>
                </MapColumn>
                {usePanel && activeLocation && videoUrl && (
                    <VideoCard ref={cardRef} key={activeLocation.fields.state}>
                        <CardHeader>
                            <CardTitle>{videoTitle}</CardTitle>
                            <CloseButton
                                type="button"
                                aria-label="Close video"
                                onClick={() => setActiveLocation(undefined)}
                            >
                                &times;
                            </CloseButton>
                        </CardHeader>
                        <VideoFrame key={videoUrl} $portrait={isPortrait(activeVideo)}>
                            {isFileVideo(videoUrl) ? (
                                <video src={videoUrl} controls autoPlay />
                            ) : (
                                <iframe
                                    src={toEmbedUrl(videoUrl)}
                                    title={videoTitle || "video"}
                                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            )}
                        </VideoFrame>
                        {activeVideos.length > 1 && (
                            <Playlist>
                                {activeVideos.map((video, index) => (
                                    <PlaylistButton
                                        key={`${video.fields.videoUrl}-${index}`}
                                        type="button"
                                        $active={index === activeVideoIndex}
                                        aria-current={index === activeVideoIndex}
                                        onClick={() => setActiveVideoIndex(index)}
                                    >
                                        {video.fields.videoTitle || `Video ${index + 1}`}
                                    </PlaylistButton>
                                ))}
                            </Playlist>
                        )}
                        {activeVideo?.fields.videoDescription && (
                            <StyledWrapper {...styles.videoDescription}>
                                <Typography variant="p">{activeVideo.fields.videoDescription}</Typography>
                            </StyledWrapper>
                        )}
                    </VideoCard>
                )}
                {connector && (
                    <ConnectorLayer aria-hidden="true">
                        <ConnectorPath
                            stroke={labelColor}
                            d={`M ${connector.x1} ${connector.y1} C ${(connector.x1 + connector.x2) / 2} ${
                                connector.y1
                            }, ${(connector.x1 + connector.x2) / 2} ${connector.y2}, ${connector.x2} ${connector.y2}`}
                        />
                        <circle cx={connector.x1} cy={connector.y1} r={4} fill={labelColor} />
                    </ConnectorLayer>
                )}
            </ContentRow>
            <Modal
                {...styles.modal}
                headline={videoTitle}
                isOpen={!usePanel && !!activeLocation}
                handleClose={() => setActiveLocation(undefined)}
            >
                {videoUrl && (
                    <VideoFrame key={videoUrl} $portrait={isPortrait(activeVideo)}>
                        {isFileVideo(videoUrl) ? (
                            <video src={videoUrl} controls autoPlay />
                        ) : (
                            <iframe
                                src={toEmbedUrl(videoUrl)}
                                title={videoTitle || "video"}
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        )}
                    </VideoFrame>
                )}
                {activeVideos.length > 1 && (
                    <Playlist>
                        {activeVideos.map((video, index) => (
                            <PlaylistButton
                                key={`${video.fields.videoUrl}-${index}`}
                                type="button"
                                $active={index === activeVideoIndex}
                                aria-current={index === activeVideoIndex}
                                onClick={() => setActiveVideoIndex(index)}
                            >
                                {video.fields.videoTitle || `Video ${index + 1}`}
                            </PlaylistButton>
                        ))}
                    </Playlist>
                )}
                {activeVideo?.fields.videoDescription && (
                    <StyledWrapper {...styles.videoDescription}>
                        <Typography variant="p">{activeVideo.fields.videoDescription}</Typography>
                    </StyledWrapper>
                )}
            </Modal>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: LocationVideoMap,
    definition: {
        group: "Common",
        displayName: "Location Video Map",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                tab: contentTab,
            },
            {
                name: fields.subheading,
                displayName: "Subheading",
                editorTemplate: "MultilineTextField",
                fieldType: "Translatable",
                defaultValue: "",
                tab: contentTab,
            },
            {
                name: fields.locations,
                displayName: "Locations",
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                getDisplay: (item: HasFields) => item.fields.label || getUsState(item.fields.state)?.name || "New Location",
                tab: contentTab,
                fieldDefinitions: [
                    {
                        name: "state",
                        displayName: "State",
                        editorTemplate: "DropDownField",
                        defaultValue: "IA",
                        options: usStateOptions,
                    },
                    {
                        name: "label",
                        displayName: "Label Override (defaults to state name)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "stateTitle",
                        displayName: "Title Shown Above Map When Selected",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "useDefaultColor",
                        displayName: "Use Default Highlight Color",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                    {
                        name: "stateColor",
                        displayName: "Custom State Fill Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: (item: HasFields) => item.fields.useDefaultColor === false,
                    },
                    {
                        name: "pinIconSize",
                        displayName: "Pin Icon Size (blank uses the default icon size)",
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                    },
                    {
                        name: "selectedColor",
                        displayName: "Selected State Fill Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },
                    {
                        name: "pinIcon",
                        displayName: "Pin Icon (blank uses a colored dot)",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "xDirection",
                        displayName: "Pin Horizontal Direction",
                        editorTemplate: "DropDownField",
                        defaultValue: "right",
                        options: [
                            { displayName: "Right", value: "right" },
                            { displayName: "Left", value: "left" },
                        ],
                    },
                    {
                        name: "xOffset",
                        displayName: "Pin Horizontal Nudge",
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                    },
                    {
                        name: "yDirection",
                        displayName: "Pin Vertical Direction",
                        editorTemplate: "DropDownField",
                        defaultValue: "down",
                        options: [
                            { displayName: "Down", value: "down" },
                            { displayName: "Up", value: "up" },
                        ],
                    },
                    {
                        name: "yOffset",
                        displayName: "Pin Vertical Nudge",
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                    },
                    {
                        name: "iconXDirection",
                        displayName: "Icon Horizontal Direction (aligns icon with pulse effect)",
                        editorTemplate: "DropDownField",
                        defaultValue: "right",
                        options: [
                            { displayName: "Right", value: "right" },
                            { displayName: "Left", value: "left" },
                        ],
                    },
                    {
                        name: "iconXOffset",
                        displayName: "Icon Horizontal Nudge",
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                    },
                    {
                        name: "iconYDirection",
                        displayName: "Icon Vertical Direction (aligns icon with pulse effect)",
                        editorTemplate: "DropDownField",
                        defaultValue: "down",
                        options: [
                            { displayName: "Down", value: "down" },
                            { displayName: "Up", value: "up" },
                        ],
                    },
                    {
                        name: "iconYOffset",
                        displayName: "Icon Vertical Nudge",
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                    },
                    {
                        name: "videos",
                        displayName: "Videos",
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields) => item.fields.videoTitle || "New Video",
                        fieldDefinitions: [
                            {
                                name: "videoTitle",
                                displayName: "Video Title",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "videoUrl",
                                displayName: "Video URL (YouTube, Vimeo or file)",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "videoDescription",
                                displayName: "Video Description",
                                editorTemplate: "MultilineTextField",
                                defaultValue: "",
                            },
                            {
                                name: "orientation",
                                displayName: "Video Orientation",
                                editorTemplate: "DropDownField",
                                defaultValue: "auto",
                                options: [
                                    { displayName: "Auto detect", value: "auto" },
                                    { displayName: "Landscape (16:9)", value: "landscape" },
                                    { displayName: "Portrait / Shorts (9:16)", value: "portrait" },
                                ],
                            },
                        ],
                    },
                    {
                        name: "hideLabel",
                        displayName: "Hide Label",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.markerIconSize,
                displayName: "Default Pin Icon Size",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 24,
                tab: settingsTab,
            },
            {
                name: fields.labelColor,
                displayName: "Label Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                defaultValue: "#00843d",
                tab: settingsTab,
            },
            {
                name: fields.extraStates,
                displayName: "Extra States To Display (no video)",
                editorTemplate: "ListField",
                fieldType: "General",
                defaultValue: [],
                getDisplay: (item: HasFields) => getUsState(item.fields.state)?.name || "New State",
                tab: contentTab,
                fieldDefinitions: [
                    {
                        name: "state",
                        displayName: "State",
                        editorTemplate: "DropDownField",
                        defaultValue: "MO",
                        options: usStateOptions,
                    },
                ],
            },
            {
                name: fields.mapScope,
                displayName: "Map Scope",
                editorTemplate: "DropDownField",
                fieldType: "General",
                defaultValue: "full",
                options: [
                    { displayName: "Full US map", value: "full" },
                    { displayName: "Only the selected states", value: "selected" },
                ],
                tab: settingsTab,
            },
            {
                name: fields.mapPadding,
                displayName: "Map Padding (selected states only)",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 20,
                isVisible: (item: HasFields) => item.fields.mapScope === "selected",
                tab: settingsTab,
            },
            {
                name: fields.videoDisplay,
                displayName: "Video Display",
                editorTemplate: "DropDownField",
                fieldType: "General",
                defaultValue: "panel",
                options: [
                    { displayName: "Side panel beside the map", value: "panel" },
                    { displayName: "Popup modal", value: "modal" },
                ],
                tab: settingsTab,
            },
            {
                name: fields.maxWidth,
                displayName: "Map Max Width (px)",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 900,
                tab: settingsTab,
            },
            {
                name: fields.stateFill,
                displayName: "Default State Fill Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                defaultValue: "#f2f2f2",
                tab: settingsTab,
            },
            {
                name: fields.stateBorder,
                displayName: "State Border Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                defaultValue: "#ffffff",
                tab: settingsTab,
            },
            {
                name: fields.highlightFill,
                displayName: "Highlighted State Fill Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                defaultValue: "#9fd6c4",
                tab: settingsTab,
            },
            {
                name: fields.selectedFill,
                displayName: "Selected State Fill Color (blank keeps the highlight color)",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                defaultValue: "",
                tab: settingsTab,
            },
            {
                name: fields.markerSize,
                displayName: "Pin Size",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 7,
                tab: settingsTab,
            },
            {
                name: fields.markerColor,
                displayName: "Pin Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                defaultValue: "#1c1c1c",
                tab: settingsTab,
            },
            {
                name: fields.markerIcon,
                displayName: "Default Pin Icon (blank uses a colored dot)",
                editorTemplate: "ImagePickerField",
                fieldType: "General",
                defaultValue: "",
                tab: settingsTab,
            },
            {
                name: fields.labelFontSize,
                displayName: "Label Font Size",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 11,
                tab: settingsTab,
            },
            {
                name: fields.showLabels,
                displayName: "Show State Labels",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
                tab: settingsTab,
            },
        ],
    },
};

export default widgetModule;
